import {
  BOSSES,
  dustForPoints,
  ELITE_DUST,
  FORKS,
  LEVEL_COST_BP,
  NIGHTMARES,
  STRUCTURES,
  WAVE,
  type StructureType,
} from '@somnhold/content';
import { panoplyDied, panoplyDue } from './panoply.ts';
import { artificerDied } from './panoply-artificer.ts';
import { mageDied } from './panoply-mage.ts';
import { artificerUpkeep, updateBots } from './bots.ts';
import { followZones, turretLinks } from './uniques.ts';
import { formsDied } from './forms.ts';
import { escortResult } from './escort.ts';
import { plunderResult, plunderTick } from './plunder.ts';
import { cluesTick } from './clues.ts';
import { crowdResult, crowdTick } from './crowd.ts';
import { rescueResult, rescueTick } from './rescue.ts';
import { herdResult, herdTick } from './herd.ts';
import { lashTick } from './lash.ts';
import { collapseResult, collapseTick } from './collapse.ts';
import { trapTick } from './trap.ts';
import { dragonResult } from './dragon.ts';
import { blastResult, blastTick, crackSealed } from './blast.ts';
import { riftSpawnAt, riftsTick } from './rifts.ts';
import { aloneLost, aloneTick } from './alone.ts';
import { railsTick } from './rails.ts';
import { bellsResult, bellsTick } from './bells.ts';
import { anvilResult, anvilTick } from './anvil.ts';
import { risingTick } from './rising.ts';
import { wellResult, wellTick } from './well.ts';
import { drinkResult } from './drinker.ts';
import { fairResult, fairTick } from './fair.ts';
import { tideResult, tideTick } from './tide.ts';
import { finResult, finTick } from './fin.ts';
import { mawResult, mawTick } from './maw.ts';
import { sunkenResult, sunkenTick } from './sunken.ts';
import { neckTick } from './neck.ts';
import { winchTick, winchResult } from './hoist.ts';
import { copiesResult, copiesTick } from './copies.ts';
import { tollResult, tollTick } from './toll.ts';
import { alliesTick } from './night.ts';
import { huntResult, huntTick } from './hunt.ts';
import { callResult, callTick } from './call.ts';
import { facetakerSpawn } from './facetaker.ts';
import { plunderBot } from '../ai/plunder-bot.ts';
import { ghostThink } from '../ai/ghost.ts';
import { cache, cellId, isBuildableTerrain, structureAtCell, terrainAt, touchGrid } from '../grid.ts';
import { DIRS, hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import type { BattleState, Command, Enemy, TickInputs } from '../types.ts';
import { damageHero, damageKeep, damageStructure, enemiesNear, ev, pstats, spawnEnemy, targetable } from './combat.ts';
import { makeStructure, spawnPoint, structureMaxHp } from './create.ts';
import { dungeonTick, holdInEntrance } from './dungeon.ts';
import { obsidianDeath, obsidianTick } from './obsidian.ts';
import { dungeonBot, isSpectre } from '../ai/dungeon-bot.ts';
import { mazeBot } from '../ai/maze-bot.ts';
import { mazeKeep } from './maze.ts';
import { cellarsBot } from '../ai/cellars-bot.ts';
import { ascuasTick } from './ascuas.ts';
import { omenTick } from './omens.ts';
import { GENERAL_PID, joinSquad, makeMove, moveReady, planTick } from './plan.ts';
import { updateEnemies } from './enemies.ts';
import { lifeAura, updateShields } from './kits.ts';
import { applyCommand, onKill, updateHeroes } from './heroes.ts';
import { sting, updateZones } from './skills.ts';
import { featFelled, featsTick } from './feats.ts';
import { isBoss, updateProjectiles, updateStructures, updateTurrets } from './towers.ts';
import { markRaised } from './frost.ts';
import { unsleepingDied, unsleepingSlain, unsleepingSpawn, unsleepingStood } from './unsleeping.ts';
import { simaDeath } from './sima.ts';
import { simaPropDied } from './sima-bosses.ts';
import { lucanCenser } from './relics.ts';

const sortedPids = (inputs: TickInputs): string[] => Object.keys(inputs).sort();

/**
 * Advances the battle one tick (SPEC §3.1). Mutates `s`. Inputs are applied in player-id order,
 * entities update in id order, and every random draw comes from `s.rng`.
 */
export const stepBattle = (s: BattleState, inputs: TickInputs = {}): void => {
  if (s.result !== 'running') return;
  s._events = [];
  s.tick++;
  for (const pid of sortedPids(inputs)) {
    const cmds = inputs[pid] as Command[];
    for (const c of cmds) applyPlayerCommand(s, pid, c);
  }
  if (!s.started) {
    // Before the fight (and in the bastion between waves) heroes still walk around; in the crypt, only
    // in its entrance (walking the halls ahead woke nothing and broke the run once it started).
    updateHeroes(s);
    holdInEntrance(s);
    cache(s);
    return;
  }
  s.waveT++;
  // Incursions: an empty field fast-forwards to the next wave, so there is no idle wait.
  const o = s.objective;
  const quiet = !!o?.quietRate && s.wave.next < s.wave.spawns.length && !s.enemies.some((e) => e.hp > 0 && e.kind !== 'boneWall');
  if (o?.quietRate) o.quiet = quiet;
  if (quiet) s.waveT += (o?.quietRate as number) - 1;
  // Era V's Dream Beacon: once a wave it pulses, and the spectres fight as if their players were there.
  const beacon = s.rules?.dreamBeacon;
  if (beacon && s.mode === 'wave') dreamPulse(s, beacon);
  // In the crypt a dropped player's hero just stands there; only test bots (mercs) and absent friends' spectres think for themselves.
  for (const h of s.heroes) {
    if (h.control === 'live') continue;
    if (s.plunder && h.control === 'merc') plunderBot(s, h);
    else if (s.mode !== 'dungeon') ghostThink(s, h);
    else if (isSpectre(s, h)) dungeonBot(s, h);
    else if (h.control === 'merc') {
      if (s.dungeon?.exp) cellarsBot(s, h);
      else if (s.dungeon?.maze) mazeBot(s, h);
      else dungeonBot(s, h);
    }
  }
  if (s.dungeon) dungeonTick(s);
  riftsTick(s);
  spawnDue(s);
  planTick(s);
  updateHeroes(s);
  updateStructures(s);
  updateShields(s);
  lifeAura(s);
  updateTurrets(s);
  turretLinks(s);
  // What the panoply's pieces left for this tick (docs/PANOPLIA.md §8).
  panoplyDue(s);
  updateBots(s);
  artificerUpkeep(s);
  updateEnemies(s);
  updateProjectiles(s);
  ascuasTick(s);
  obsidianTick(s);
  omenTick(s);
  plunderTick(s);
  cluesTick(s);
  crowdTick(s);
  rescueTick(s);
  herdTick(s);
  lashTick(s);
  collapseTick(s);
  trapTick(s);
  blastTick(s);
  aloneTick(s);
  railsTick(s);
  winchTick(s);
  copiesTick(s);
  tollTick(s);
  alliesTick(s);
  bellsTick(s);
  anvilTick(s);
  risingTick(s);
  wellTick(s);
  fairTick(s);
  tideTick(s);
  finTick(s);
  sunkenTick(s);
  neckTick(s);
  mawTick(s);
  huntTick(s);
  callTick(s);
  followZones(s);
  updateZones(s);
  featsTick(s);
  reap(s);
  if (s.dungeon?.maze) mazeKeep(s, s.dungeon.maze);
  if (s.objective) {
    s.objective.progress = imin(s.objective.total, s.objective.progress + (quiet ? (o?.quietRate as number) : 1));
    // Loaded up, but nightmares still standing: the golems wait (newer incursions).
    // They wait for the field, and whoever was shooting from afar (out of a hero's reach) comes in now.
    if (s.rules?.clearToFinish && s.objective.progress >= s.objective.total && s.enemies.some((e) => e.hp > 0 && e.kind !== 'boneWall')) {
      s.objective.progress = s.objective.total - 1;
      s.wave.lastStand = true;
    }
  }
  checkEnd(s);
  // A grid change in this tick is taken into the paths now, inside the tick: never later from a screen
  // asking where it can walk, which would weigh the paths by a different moment's health (grid.ts).
  cache(s);
};

const applyPlayerCommand = (s: BattleState, pid: string, c: Command): void => {
  if (c.t === 'start') {
    s.started = true;
    return;
  }
  if (c.t === 'place' || c.t === 'sell' || c.t === 'upgrade') {
    sandbox(s, c);
    return;
  }
  // The enemy general's move (only from it, and only the one legal in that sector now).
  if (c.t === 'order') {
    // A live general can't send the bulk before half of it is out (the heuristic's own moves aren't orders).
    const early = (c.move === 'assault' || c.move === 'strike') && !moveReady(s, c.sector);
    if (pid === GENERAL_PID && s.started && !early) makeMove(s, c.sector, c.move, 'general', { ...(c.target ? { target: c.target } : {}), ...(typeof c.spot === 'number' ? { spot: c.spot } : {}), ...(c.to !== undefined ? { to: c.to } : {}) });
    return;
  }
  const h = s.heroes.find((x) => x.pid === pid);
  if (h) applyCommand(s, h, c);
};

/** Practice mode: instant building with a purse. */
const sandbox = (s: BattleState, c: Extract<Command, { t: 'place' | 'sell' | 'upgrade' }>): void => {
  if (!s.purse) return;
  const id = cellId(s.map.radius, c.q, c.r);
  if (id < 0) return;
  const existing = structureAtCell(s, id);
  if (c.t === 'place') {
    if (existing || !isBuildableTerrain(terrainAt(s, c.q, c.r))) return;
    // Gates stand where the ring put them, except on terraced land, where the group sets its own.
    if (!(c.s in STRUCTURES) || (c.s === 'gate' && !s.map.land)) return;
    const p = hexToWorld(c.q, c.r);
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, p.x, p.y, 520)) return;
    for (const e of s.enemies) if (e.hp > 0 && withinRange(e.x, e.y, p.x, p.y, 520)) return;
    const cost = STRUCTURES[c.s as StructureType].cost;
    if (!pay(s, cost, 10000)) return;
    const raised = makeStructure(s, { type: c.s, level: 1, material: 1, q: c.q, r: c.r });
    s.structures.push(raised);
    markRaised(s, raised);
    s.structures.sort((a, b) => cellId(s.map.radius, a.q, a.r) - cellId(s.map.radius, b.q, b.r));
    touchGrid(s);
    ev(s, { t: 'struct', id: s.nextId - 1, what: 'built' });
    return;
  }
  if (!existing || (existing.type === 'gate' && !s.map.land)) return;
  const def = STRUCTURES[existing.type];
  if (c.t === 'sell') {
    const refund = mulBp(def.cost.wood, 5000);
    s.purse.wood += refund;
    s.purse.stone += mulBp(def.cost.stone, 5000);
    s.purse.crystal += mulBp(def.cost.crystal, 5000);
    s.structures = s.structures.filter((x) => x.id !== existing.id);
    touchGrid(s);
    return;
  }
  if (existing.level >= 2 || existing.destroyed) return; // level 3 needs an artificer in the bastion
  if (!pay(s, def.cost, (LEVEL_COST_BP[1] as number) - 10000)) return;
  const ratio = existing.maxHp > 0 ? Math.trunc((existing.hp * 10000) / existing.maxHp) : 10000;
  existing.level = 2;
  existing.maxHp = structureMaxHp(existing.type, 2, existing.material, existing.fortified);
  existing.hp = mulBp(existing.maxHp, ratio);
  if (def.charges) existing.charges = def.charges;
  ev(s, { t: 'struct', id: existing.id, what: 'built' });
};

const pay = (s: BattleState, cost: { wood: number; stone: number; crystal: number }, bp: number): boolean => {
  const p = s.purse;
  if (!p) return false;
  const w = mulBp(cost.wood, bp);
  const st = mulBp(cost.stone, bp);
  const cr = mulBp(cost.crystal, bp);
  if (p.wood < w || p.stone < st || p.crystal < cr) return false;
  p.wood -= w;
  p.stone -= st;
  p.crystal -= cr;
  return true;
};

const spawnDue = (s: BattleState): void => {
  const w = s.wave;
  while (w.next < w.spawns.length) {
    const sp = w.spawns[w.next] as (typeof w.spawns)[number];
    if (sp.tick > s.waveT) break;
    // A hundred at a blow: the packs wait while the field is this full (for the phones, and so it stays a clearing).
    if (s.rules?.burst && s.enemies.length >= s.rules.burst.alive) break;
    w.next++;
    // The blasting: nothing comes out of a sealed crack.
    if (crackSealed(s, sp.crack)) continue;
    const p = sp.at ? hexToWorld(sp.at.q, sp.at.r) : (riftSpawnAt(s, sp) ?? spawnPoint(s, sp.sector));
    const jitter = 250;
    const isBoss = sp.kind in BOSSES;
    const e = spawnEnemy(
      s,
      sp.kind,
      sp.tier,
      sp.elite,
      sp.sector,
      p.x + rngRange(s.rng, -jitter, jitter),
      p.y + rngRange(s.rng, -jitter, jitter),
      sp.hp ?? (isBoss ? w.bossHp : 0),
    );
    if (sp.boss) s.bossId = e.id;
    if (sp.un) unsleepingSpawn(s, e, sp);
    if (sp.copy) facetakerSpawn(s, e, sp);
    if (sp.strengthBp) {
      e.hp = e.maxHp = mulBp(e.maxHp, sp.strengthBp);
      e.damage = mulBp(e.damage, sp.strengthBp);
    }
    if (sp.rally) e.rally = cellId(s.map.radius, sp.rally.q, sp.rally.r);
    if (sp.squad !== undefined && s.plan) {
      e.squad = sp.squad;
      joinSquad(s, e);
    }
    const r = s.rules;
    if (r?.enemyHpBp && !sp.hp) {
      e.maxHp = imax(1, mulBp(e.maxHp, r.enemyHpBp));
      e.hp = e.maxHp;
    }
    if (r?.enemySpeedBp) e.speed = mulBp(e.speed, r.enemySpeedBp);
    if (r?.eliteHpBp && e.elite && !sp.hp) {
      e.maxHp = mulBp(e.maxHp, r.eliteHpBp);
      e.hp = e.maxHp;
    }
  }
};

/** Removes dead nightmares: rewards, splits, kill credit. */
const reap = (s: BattleState): void => {
  let any = false;
  for (const e of s.enemies) if (e.hp <= 0) any = true;
  if (!any) return;
  const alive: Enemy[] = [];
  const dead: Enemy[] = [];
  for (const e of s.enemies) (e.hp <= 0 ? dead : alive).push(e);
  s.enemies = alive;
  for (const e of dead) onDeath(s, e);
};

/** A hundred at a blow: the kills inside the window, and the most there ever were at once. */
const burstKill = (s: BattleState, window: number): void => {
  const b = (s.burst ??= { kills: [], best: 0 });
  b.kills.push(s.tick);
  let drop = 0;
  while (drop < b.kills.length && (b.kills[drop] as number) <= s.tick - window) drop++;
  if (drop) b.kills.splice(0, drop);
  if (b.kills.length > b.best) b.best = b.kills.length;
};

const onDeath = (s: BattleState, e: Enemy): void => {
  const d = NIGHTMARES[e.kind];
  ev(s, { t: 'die', id: e.id, kind: e.kind, x: e.x, y: e.y });
  // The Chasm: echoes, embers, bursting puffballs, broken ice (docs/SIMA.md).
  if (s.dungeon?.sima) {
    simaPropDied(s, e);
    simaDeath(s, e);
  }
  if (e.kind === 'boneWall') {
    touchGrid(s);
    return;
  }
  // One of the Unsleeping: fallen for good, or faded into smoke (no reward: he comes back).
  if (e.un) {
    unsleepingSlain(s, e);
    if (e.fled) return;
  } else if (s.un) unsleepingDied(s, e);
  // A big boss felled, for the unique pieces' Legend (docs/UNICAS.md §2.2).
  featFelled(s, e);
  // The old forest (docs/UNICAS.md §3): what dies held leaves a root.
  formsDied(s, e);
  // The butcher's cleaver (docs/PANOPLIA.md §10): its mark jumps to the next.
  panoplyDied(s, e);
  artificerDied(s, e);
  mageDied(s, e);
  s.stats.kills++;
  // Lucan's censer: a death near its carrier mends the heroes around it.
  lucanCenser(s, e);
  // Only what the heroes fell counts toward a hundred at a blow (not what breaks itself on the node).
  if (s.rules?.burst && e.lastPid) burstKill(s, s.rules.burst.ticks);
  // Jumping wasps: what's left of their sting flies to the nearest nightmare.
  if (e.wasps?.jump && e.dotTicks > 0) {
    const next = enemiesNear(s, e.x, e.y, FORKS.jumpingWasps.radius, (o) => o.hp > 0 && targetable(s, o))[0];
    if (next) {
      sting(next, e.dotDmg, e.dotEvery, e.dotTicks, e.lastPid ?? '', true, e.wasps.dt ?? 'poison');
      ev(s, { t: 'boom', x: next.x, y: next.y, r: 500, kind: 'wasps' });
    }
  }
  const ss = s.stats.sectors[e.sector];
  if (ss) ss.kills++;
  let dust = d.points > 0 ? dustForPoints(d.points) : 0;
  if (e.elite) {
    dust += ELITE_DUST;
    s.stats.eliteKills++;
    s.stats.highlights.push({ tick: s.tick, kind: 'eliteSlain', ...(e.lastPid ? { pid: e.lastPid } : {}), sector: e.sector, what: e.kind, ...(e.tier >= 2 ? { ink: true } : {}) });
  }
  if (e.kind === 'witch' || e.kind === 'shell') {
    s.stats.bossKilled = true;
    dust += 100;
    s.stats.highlights.push({ tick: s.tick, kind: 'bossSlain', ...(e.lastPid ? { pid: e.lastPid } : {}), sector: e.sector, what: e.kind });
  }
  s.stats.dust += dust;
  if (e.lastPid) {
    pstats(s, e.lastPid).kills++;
    onKill(s, e.lastPid, e);
  }
  obsidianDeath(s, e);
  // A bannerman falling: the pack around it falters.
  if (d.banner) {
    for (const o of s.enemies) {
      if (o.id === e.id || o.hp <= 0 || !withinRange(e.x, e.y, o.x, o.y, d.banner.radius)) continue;
      if (o.slowBp < d.banner.slowBp) o.slowBp = d.banner.slowBp;
      if (o.slowTicks < d.banner.falterTicks) o.slowTicks = d.banner.falterTicks;
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: d.banner.radius, kind: 'falter' });
  }
  // A bonesinger near: the fallen gets back up as a skeleton (the nearest singer with songs left, lowest id on a tie).
  if (e.kind !== 'bonesinger' && e.elite === false && !e.un && !isBoss(e) && e.kind !== 'warden' && e.kind !== 'magmaLord' && e.kind !== 'colossus' && e.kind !== 'dragon') {
    let singer: Enemy | null = null;
    let bd = 0x7fffffff;
    for (const o of s.enemies) {
      const r = NIGHTMARES[o.kind].raises;
      if (!r || o.hp <= 0 || (o.raised ?? 0) >= r.max || !withinRange(e.x, e.y, o.x, o.y, r.radius)) continue;
      const dd = dist2(e.x, e.y, o.x, o.y);
      if (dd < bd) {
        bd = dd;
        singer = o;
      }
    }
    if (singer) {
      singer.raised = (singer.raised ?? 0) + 1;
      const up = spawnEnemy(s, 'skeleton', e.tier, false, e.sector, e.x, e.y);
      up.next = e.next;
      ev(s, { t: 'mend', from: singer.id, id: up.id, amount: 0, x: e.x, y: e.y });
      ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'raise' });
    }
  }
  // A weaver bursts into spiderlings.
  if (d.brood) {
    for (let i = 0; i < d.brood.count; i++) {
      const a = DIRS[i % DIRS.length] as { q: number; r: number };
      const o = hexToWorld(a.q, a.r);
      const sp = spawnEnemy(s, 'spiderling', e.tier, false, e.sector, e.x + idiv(o.x * 3, 10), e.y + idiv(o.y * 3, 10));
      sp.next = e.next;
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 700, kind: 'brood' });
  }
  // Burst on death: hurts heroes and structures around it (a pumpkin).
  if (d.deathBlast) {
    const b = d.deathBlast;
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, b.radius)) damageHero(s, h, b.heroDamage);
    for (const st of s.structures) {
      if (st.destroyed || st.maxHp <= 0) continue;
      const c = hexToWorld(st.q, st.r);
      if (withinRange(e.x, e.y, c.x, c.y, b.radius)) damageStructure(s, st, b.structureDamage);
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: b.radius, kind: e.kind });
  }
  if (d.splitCount > 0 && !e.split) {
    for (let i = 0; i < d.splitCount; i++) {
      const child = spawnEnemy(
        s,
        e.kind,
        e.tier,
        false,
        e.sector,
        e.x + rngRange(s.rng, -300, 300),
        e.y + rngRange(s.rng, -300, 300),
        mulBp(e.maxHp, d.splitHpBp),
      );
      child.split = true;
      child.next = e.next;
      // Down in the cellars a nightmare never leaves its room: nor do its halves.
      if (e.room !== undefined) child.room = e.room;
    }
  }
};

const checkEnd = (s: BattleState): void => {
  if (s.dungeon) {
    // The crypt is won when the Warden falls and lost when nobody is left standing.
    const x = s.dungeon.exp;
    if (s.heroes.length && s.heroes.every((h) => h.downTicks > 0)) {
      if (x) x.lost = 'fallen';
      finish(s, 'lost');
    } else if (s.rules?.race && s.started && s.waveT >= s.rules.race.limit && s.result === 'running') {
      // The race: the clock ran out before the Warden fell.
      finish(s, 'lost');
    } else if (s.rules?.drinker) {
      // El pozo 2: the Drinker down is only half of it; everyone out of the water wins it.
      const r = drinkResult(s);
      if (r) finish(s, r);
    } else if (s.dungeon.maze) {
      // The Furrows: back by a rope, the torch out or the centre taken all keep what was found.
      if (s.dungeon.maze.outcome) finish(s, 'won');
    } else if (x) {
      if (x.raised) finish(s, 'won');
      else if (x.lost) finish(s, 'lost');
    } else if (s.dungeon.sima) {
      // The Chasm: it has no bottom; it is won by walking out with the chest (falling ends it lost, with half).
      if (s.dungeon.sima.left) finish(s, 'won');
    } else if (s.dungeon.deep) {
      // The deep crypt: won by walking out with the chest, or by felling the last floor's guardian.
      const dp = s.dungeon.deep;
      if (dp.left) finish(s, 'won');
      else if (dp.floor === dp.floors - 1 && dp.guardian > 0 && !s.enemies.some((e) => e.id === dp.guardian && e.hp > 0)) {
        dp.cleared = dp.floors;
        finish(s, 'won');
      }
    } else if (s.dungeon.reached) finish(s, 'won');
    else if (s.dungeon.boss > 0 && !s.enemies.some((e) => e.id === s.dungeon?.boss && e.hp > 0)) finish(s, 'won');
    return;
  }
  if (s.plunder) {
    // A plunder: it ends when the group leaves, the golem does, or everyone falls.
    const r = plunderResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.escort) {
    // An escort: only the caravan decides it.
    const r = escortResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.copies) {
    // The copies: every one felled wins it; the post falling or the outing over loses it.
    const r = copiesResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.toll) {
    // The bell at midnight: the calm full wins it, the rope cut loses it.
    const r = tollResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.winch) {
    // Hanging the bell: wound all the way up wins it, the frame falling loses it.
    const r = winchResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.blast) {
    // The blasting: the beacon lit wins it, the tower falling loses it.
    const r = blastResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.sunken?.boss) {
    // The Drowned Dragon: felled wins it, the stilt-houses falling lose it (before the fin's dawn, which never comes).
    const r = sunkenResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.maw) {
    // The Maw: felled wins it, the lighthouse falling loses it.
    const r = mawResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.fin) {
    // The fin: dawn wins it, the stilt-houses falling lose it.
    const r = finResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.tide) {
    // The low water: the net home wins it; the net washed away or the node fallen loses it.
    const r = tideResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.fair) {
    // Fair day: it always runs its time; what was saved says how big the fair is.
    const r = fairResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.well) {
    // The ink in the water: the well sealed wins it, too many houses burned loses it.
    const r = wellResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.anvil) {
    // The five strikes: five on the beat win it, the Anvil falling loses it.
    const r = anvilResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.bells) {
    // The alarm bells: every lever down wins it, the node falling loses it.
    const r = bellsResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.dragon) {
    // The dragon of the Burn: no node in its clearing; it falling wins it, everyone down at once or the glass loses it.
    const r = dragonResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.trap) {
    // The old trap: no node out there; the colossus falling wins it, the day running out loses it.
    if (s.bossId && !s.enemies.some((e) => e.id === s.bossId && e.hp > 0)) finish(s, 'won');
    else if (s.waveT >= s.rules.trap.out) finish(s, 'lost');
    return;
  }
  if (s.rules?.collapse) {
    // The collapse: only the patrol decides it (no node to hold out there).
    const r = collapseResult(s);
    if (r) finish(s, r);
    return;
  }
  const target = s.objective ?? s.keep;
  if (target.hp <= 0) {
    finish(s, 'lost');
    return;
  }
  const scratched = s.rules?.unscathed ? s.structures.find((st) => (st.type === 'wall' || st.type === 'gate') && st.hp < st.maxHp) : undefined;
  if (scratched) {
    // Not a scratch (a bet): one blow on Hilde's stone loses it.
    const c = hexToWorld(scratched.q, scratched.r);
    ev(s, { t: 'scratch', x: c.x, y: c.y });
    finish(s, 'lost');
    return;
  }
  if (aloneLost(s)) {
    // One alone (a bet): too many got past the stakes.
    finish(s, 'lost');
    return;
  }
  if (s.rules?.rescue) {
    // The pit in the woods: everyone home wins it, one falling loses it.
    const r = rescueResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.herd) {
    // The drive: the herd decides it (too few left to make it loses it).
    const r = herdResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.crowd) {
    // The night of lanterns: the crowd decides it (an impostor getting away loses it).
    const r = crowdResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.burst) {
    // A hundred at a blow: won the moment enough fall together; the hold running out (or emptying) loses it.
    if ((s.burst?.best ?? 0) >= s.rules.burst.count) finish(s, 'won');
    else if (s.objective && s.objective.progress >= s.objective.total) finish(s, 'lost');
    return;
  }
  if (s.rules?.hunt) {
    // The hunt: the Uro falling wins it; the outing running out (or ending at the camp) closes it, wounds kept.
    const r = huntResult(s);
    if (r) finish(s, r);
    return;
  }
  if (s.rules?.call && callResult(s)) {
    // Ragna knelt at the clan's call: won without her falling.
    finish(s, 'won');
    return;
  }
  if (s.rules?.bossWins) {
    // A saga boss act: only its boss falling ends it well.
    if (s.bossId && !s.enemies.some((e) => e.id === s.bossId && e.hp > 0)) finish(s, 'won');
    return;
  }
  if (s.objective) {
    // With the new pace, beating the last wave completes the extraction at once.
    const cleared = !!s.objective.quietRate && s.wave.next >= s.wave.spawns.length && !s.enemies.some((e) => e.hp > 0 && e.kind !== 'boneWall');
    if (cleared) s.objective.progress = s.objective.total;
    if (s.objective.progress >= s.objective.total) finish(s, 'won');
    return;
  }
  const allSpawned = s.wave.next >= s.wave.spawns.length;
  const remaining = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall');
  if (allSpawned && remaining.length === 0) {
    finish(s, 'won');
    return;
  }
  // The rest has fallen: the ones shooting from afar stop and come in, instead of keeping the wave going to the cap.
  if (allSpawned && !s.wave.lastStand && s.stats.kills > 0 && remaining.every((e) => NIGHTMARES[e.kind].lobs || NIGHTMARES[e.kind].shootsTowers || NIGHTMARES[e.kind].shootsHeroes)) s.wave.lastStand = true;
  if (s.waveT >= WAVE.maxTicks) {
    // Time's up: whatever is left throws itself at the keep (SPEC §8.1).
    let dmg = 0;
    for (const e of remaining) if (!NIGHTMARES[e.kind].avoidsKeep) dmg += e.damage * WAVE.capHits;
    for (let i = s.wave.next; i < s.wave.spawns.length; i++) {
      const sp = s.wave.spawns[i] as (typeof s.wave.spawns)[number];
      dmg += NIGHTMARES[sp.kind].damage * WAVE.capHits;
    }
    damageKeep(s, dmg, null);
    finish(s, s.keep.hp <= 0 ? 'lost' : 'won');
  }
};

const finish = (s: BattleState, r: 'won' | 'lost'): void => {
  if (s.un) unsleepingStood(s);
  s.result = r;
  s.endTick = s.tick;
};

/** Runs a battle to the end with a per-tick input provider. */
export const runBattle = (
  s: BattleState,
  inputsAt: (tick: number) => TickInputs | undefined = () => undefined,
  maxTicks = WAVE.maxTicks + 600,
): BattleState => {
  let guard = 0;
  while (s.result === 'running' && guard++ < maxTicks) stepBattle(s, inputsAt(s.tick + 1) ?? {});
  return s;
};


/** The Dream Beacon's pulse: it lifts the spectres' power for a while, then lets it fall back. */
const dreamPulse = (s: BattleState, b: { at: number; ticks: number; powerBp: number }): void => {
  if (s.waveT === b.at) {
    let lifted = false;
    for (const h of s.heroes) {
      if (h.control !== 'ghost' || h.stats.powerBp >= b.powerBp) continue;
      h.dreamBase = h.stats.powerBp;
      h.stats.powerBp = b.powerBp;
      lifted = true;
    }
    if (lifted) ev(s, { t: 'boom', x: 0, y: 0, r: 6000, kind: 'dreamBeacon' });
  } else if (s.waveT === b.at + b.ticks) {
    for (const h of s.heroes) {
      if (h.dreamBase === undefined) continue;
      h.stats.powerBp = h.dreamBase;
      delete h.dreamBase;
    }
  }
};
